export type ReportStatus = 
  | 'not_submitted'
  | 'pending_review'
  | 'in_review'
  | 'approved'
  | 'rejected';

export interface QuarterlyReport {
  id: string;
  name: string;
  title: string;
  dueDate: string;
  status: ReportStatus;
  isInProgress?: boolean;
  /** Present on admin cross-org list rows (`GET /v1/admin/reports`). */
  subawardee?: string;
  reportingYear?: number;
}

export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  uploadDate: string;
  url: string;
  type: string;
  contentType?: string;
}

export interface TemplateFile {
  name: string;
  gcs_path: string;
}

export interface TemplateResponse {
  template_id: string;
  template_name: string;
  files: TemplateFile[];
}

export interface Metrics {
  totalReports: number;
  submittedReports: number;
  completionRate: number;
  // Additional metrics for different groups
  testingCoverage?: number;
  activeClients?: number;
}

export interface ChartData {
  monthlyData: {
    name: string;
    value: number;
  }[];
  pieData: {
    name: string;
    value: number;
    color: string;
  }[];
  lineData: {
    date: string;
    submissions: number;
  }[];
  // Additional data for Skyway users
  clientData?: {
    month: string;
    clients: number;
  }[];
  // Additional data for DronePort users
  testingData?: {
    category: string;
    value: number;
  }[];
  securityData?: {
    date: string;
    coverage: number;
  }[];
}

export interface ReportQuestion {
  id: number;
  text: string;
  applicable_to: string[];
  type: 'Text' | 'Number' | 'SingleSelect' | 'MultiSelect' | 'Currency' | 'Date';
  required: boolean;
  options?: string[];
  allow_other?: boolean;
  validation?: {
    min_value?: number;
    max_value?: number;
    max_value_from_question?: number;
    /**
     * Opt-in child-sum validation. When set, answers sharing the same
     * max_value_from_question + sum_group must sum to the parent
     * (e.g. "race/ethnicity" vs "gender"). Without sum_group, only the
     * ≤ parent cap from max_value_from_question applies.
     */
    sum_group?: string;
    min_selections?: number;
    max_selections?: number;
    multiline?: boolean;
  };
  conditional_logic?: {
    show_if?: {
      question_id: number;
      operator: 'greater_than' | 'less_than' | 'equals' | 'not_equals' | 'contains';
      value: number | string;
    };
    repeat_based_on?: number;
    substitute_index?: boolean;
  };
}

export interface ReportAnswer {
  question_id: number;
  value: any;
  repeat_index?: number;
}

export interface ReportSection {
  id: string;
  title: string;
  description: string;
  question_ids: number[];
}

export interface ReportTemplate {
  _id: string;
  template_id: string;
  name: string;
  description: string;
  version: number;
  is_active: boolean;
  sections: ReportSection[];
  questions: ReportQuestion[];
}

export interface ReportResponse {
  id: string;
  template_id: string;
  subawardee_id: string;
  /** User-facing title; same source as quarterly report cards when set. */
  report_title?: string | null;
  report_name?: string | null;
  submission_status: {
    state: ReportStatus;
    updated_at: string;
    updated_by: string;
    notes: string;
  };
  answers: ReportAnswer[];
  due_date: string;
  metadata: {
    created_at: string;
    created_by: string;
    updated_at: string;
    updated_by: string;
    submitted_at: string | null;
    submitted_by: string | null;
    approved_at: string | null;
    approved_by: string | null;
    rejected_at: string | null;
    rejected_by: string | null;
    rejection_reason: string | null;
  };
  template: {
    name: string;
    description: string;
    sections: ReportSection[];
    questions: ReportQuestion[];
  };
}

/** Matches list endpoints: `report_title ?? report_name` (falls back to template name). */
export function getReportDisplayTitle(report: ReportResponse): string {
  return report.report_title ?? report.report_name ?? report.template.name;
}